import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SpinnerGap } from "@phosphor-icons/react";
import { Logo, LogoMark } from "@/components/Logo";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", monthly_income: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register({
        name: form.name,
        email: form.email,
        password: form.password,
        monthly_income: Number(form.monthly_income) || 0,
      });
      toast.success("Account created!");
      navigate("/app");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 text-primary-foreground relative overflow-hidden bg-slate-900">
        <img
          src="https://images.unsplash.com/photo-1767424412548-1a1ac7f4b9bc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHw0fHxzdG9jayUyMG1hcmtldCUyMHRyYWRpbmclMjBjYW5kbGVzdGljayUyMGNoYXJ0JTIwc2NyZWVufGVufDB8fHx8MTc4ODI0MDMzOHww&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-45 kenburns"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/95 via-slate-900/85 to-indigo-900/70" />
        <Link to="/" className="flex items-center gap-2.5 relative z-10">
          <LogoMark size={30} />
          <span className="font-heading font-bold text-xl text-white">Inves<span className="text-indigo-400">y</span></span>
        </Link>
        <div className="relative z-10">
          <h2 className="font-heading font-bold text-4xl tracking-tight leading-tight">
            Take control of your money in minutes.
          </h2>
          <p className="mt-4 text-primary-foreground/80 max-w-sm">
            Set up your profile, add your income, and let Invesy do the heavy lifting.
          </p>
        </div>
        <div className="absolute -bottom-16 -right-16 h-64 w-64 rounded-full bg-accent/30 blur-3xl" />
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8">
            <Logo size={30} />
          </div>
          <h1 className="font-heading font-bold text-3xl tracking-tight">Create account</h1>
          <p className="text-muted-foreground mt-1 mb-8">Start deciding smarter today.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="name">Full name</Label>
              <Input id="name" data-testid="register-name" value={form.name} onChange={set("name")}
                required className="mt-1.5 p-4 h-12 rounded-lg" placeholder="Aarav Sharma" />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="register-email" value={form.email}
                onChange={set("email")} required className="mt-1.5 p-4 h-12 rounded-lg"
                placeholder="you@example.com" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" data-testid="register-password" value={form.password}
                onChange={set("password")} required minLength={6}
                className="mt-1.5 p-4 h-12 rounded-lg" placeholder="At least 6 characters" />
            </div>
            <div>
              <Label htmlFor="income">Monthly income (₹) <span className="text-muted-foreground font-normal">optional</span></Label>
              <Input id="income" type="number" data-testid="register-income" value={form.monthly_income}
                onChange={set("monthly_income")} className="mt-1.5 p-4 h-12 rounded-lg font-mono"
                placeholder="e.g. 80000" />
            </div>
            <Button type="submit" disabled={loading} data-testid="register-submit"
              className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 gap-2">
              {loading && <SpinnerGap size={18} className="animate-spin" />}
              Create account
            </Button>
          </form>

          <p className="text-sm text-muted-foreground mt-6 text-center">
            Already have an account?{" "}
            <Link to="/login" className="text-primary font-semibold hover:underline">
              Log in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
